import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { resetPassword } from '../api/auth';
import BrandLockup from '../components/BrandLockup';
import { Button } from '../components/ui/Button';
import PasswordInput from '../components/ui/PasswordInput';
import { useAuth } from '../context/AuthContext';
import { appTheme, pitchTheme } from '../styles/theme';
import {
  validatePasswordConfirmation,
  validateRegisterPassword,
} from '../utils/authValidation';

const Page = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px 64px;
  background: ${appTheme.pageBg};
`;

const Card = styled.div`
  width: min(420px, 100%);
  padding: 32px 28px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  box-shadow: ${appTheme.shadowMd};
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: 1.375rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${appTheme.textMuted};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.text};
`;

const FieldError = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${appTheme.dangerText};
`;

const ErrorMessage = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  border: 1px solid ${appTheme.dangerBorder};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const SuccessMessage = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.successBg};
  border: 1px solid ${appTheme.border};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.greenDark};
`;

const FullWidthButton = styled(Button)`
  width: 100%;
`;

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { applyUser } = useAuth();
  const token = searchParams.get('token')?.trim() ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || !token) {
      return;
    }

    const nextPasswordError = validateRegisterPassword(password);
    const nextConfirmError = validatePasswordConfirmation(password, confirmPassword);
    setPasswordError(nextPasswordError);
    setConfirmError(nextConfirmError);

    if (nextPasswordError || nextConfirmError) {
      setError(null);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const user = await resetPassword({ token, newPassword: password });
      applyUser(user);
      setSuccess(true);
      window.setTimeout(() => {
        navigate('/');
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.invalidResetToken'));
      setIsSubmitting(false);
    }
  };

  return (
    <Page>
      <Card>
        <BrandLockup logoHeight={24} />
        <Intro>
          <Title>{t('auth.resetPassword')}</Title>
          <Subtitle>{t('auth.resetPasswordSubtitle')}</Subtitle>
        </Intro>

        {!token ? (
          <ErrorMessage role="alert">{t('auth.resetTokenMissing')}</ErrorMessage>
        ) : success ? (
          <SuccessMessage role="status">{t('auth.resetPasswordSuccess')}</SuccessMessage>
        ) : (
          <Form noValidate onSubmit={handleSubmit}>
            <Field>
              <FieldLabel htmlFor="reset-password">{t('auth.newPassword')}</FieldLabel>
              <PasswordInput
                id="reset-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setPasswordError(null);
                }}
                onBlur={() => setPasswordError(validateRegisterPassword(password))}
                placeholder={t('auth.passwordPlaceholderRegister')}
                autoComplete="new-password"
                $invalid={Boolean(passwordError)}
                aria-invalid={Boolean(passwordError)}
                aria-describedby={passwordError ? 'reset-password-error' : undefined}
              />
              {passwordError && (
                <FieldError id="reset-password-error" role="alert">
                  {t(passwordError)}
                </FieldError>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="reset-password-confirm">{t('auth.confirmNewPassword')}</FieldLabel>
              <PasswordInput
                id="reset-password-confirm"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setConfirmError(null);
                }}
                onBlur={() =>
                  setConfirmError(validatePasswordConfirmation(password, confirmPassword))
                }
                placeholder={t('auth.passwordPlaceholderRegister')}
                autoComplete="new-password"
                $invalid={Boolean(confirmError)}
                aria-invalid={Boolean(confirmError)}
                aria-describedby={confirmError ? 'reset-password-confirm-error' : undefined}
              />
              {confirmError && (
                <FieldError id="reset-password-confirm-error" role="alert">
                  {t(confirmError)}
                </FieldError>
              )}
            </Field>

            {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

            <FullWidthButton type="submit" $variant="primary" $size="lg" disabled={isSubmitting}>
              {isSubmitting ? t('auth.pleaseWait') : t('auth.resetPasswordButton')}
            </FullWidthButton>
          </Form>
        )}
      </Card>
    </Page>
  );
};

export default ResetPasswordPage;
